/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '@ion/applications/src/store/helpers';
import {PageState} from '@ion/applications/src/types/pages';
import {error} from '@ion/core/src/logger/logger';
import {
  loadAlertNotification
} from '@ion/core/src/services/Connect';
import {
  AlertNotification,
  AlertNotifications,
  NotificationsFilters
} from '@ion/core/src/types/entities';
import {LoadAlertsNotificationParams, ServiceError} from '@ion/core/src/types/services';
import {BasemapTypes} from '@ion/features/src/maps/types/types';
import {createModularSearchFilters} from '@ion/features/src/search/utils';
import {
  AsyncState,
  PageInitializeAction
} from '@/types/redux';
import {gridDefaultPageSize} from '../../utils/helpers';

// name
export const NAME                                = 'notifications';

// initialise the grid
export const INITIALIZE_NOTIFICATION             = 'INITIALIZE_NOTIFICATION';
export const SET_GRID_COLUMNS                    = 'SET_NOTIFICATION_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS                 = 'UPDATE_NOTIFICATION_GRID_COLUMNS';

// refresh grid ...
export const NOTIFICATION_GRID_REFRESHED         = 'NOTIFICATION_GRID_REFRESHED';
export const SET_VIEW_SUB_GROUPS                 = 'SET_NOTIFICATION_VIEW_SUB_GROUPS';

export const SET_GRID_PAGE_SIZE                  = 'SET_NOTIFICATION_GRID_PAGE_SIZE';

// retrieve Alert notifications
export const GET_NOTIFICATION_REQUEST            = 'GET_NOTIFICATION_REQUEST';
export const GET_NOTIFICATION_RESPONSE           = 'GET_NOTIFICATION_RESPONSE';
export const GET_NOTIFICATION_ERROR              = 'GET_NOTIFICATION_ERROR';

// View Alert notifications
export const VIEW_NOTIFICATION_REQUEST           = 'VIEW_NOTIFICATION_REQUEST';
export const VIEW_NOTIFICATION_RESPONSE          = 'VIEW_NOTIFICATION_RESPONSE';
export const VIEW_NOTIFICATION_ERROR             = 'VIEW_NOTIFICATION_ERROR';
export const VIEW_NOTIFICATION                   = 'VIEW_NOTIFICATION';

// View map
export const SET_MAP_INITIALIZED                 = 'SET_MAP_INITIALIZED';
export const SET_MAP_BASEMAP                     = 'SET_MAP_BASEMAP';
export const SET_MAP_EXTENT                      = 'SET_MAP_EXTENT';
export const SET_MAP_ZOOM                        = 'SET_MAP_ZOOM';

// search queries
export const UPDATE_NOTIFICATIONS_SEARCH_QUERIES = 'UPDATE_NOTIFICATIONS_SEARCH_QUERIES';
export const RESET_NOTIFICATIONS_SEARCH_QUERIES  = 'RESET_NOTIFICATIONS_SEARCH_QUERIES';

/**
 * @notificationGridRefreshed
 */
export const notificationGridRefreshed = (): PageInitializeAction => {
  return {type: NOTIFICATION_GRID_REFRESHED};
};

/**
 * @initialize
 */
export const initializeNotification = (): PageInitializeAction => {
  return {type: INITIALIZE_NOTIFICATION};
};

export const setMapInitialized = () => ({
  type : SET_MAP_INITIALIZED
});

export const setMapBasemap = (basemap: any) => ({
  type : SET_MAP_BASEMAP, basemap
});

export const setMapExtent = (extent: any) => ({
  type : SET_MAP_EXTENT, extent
});

export const setMapZoom = (zoom: number) => ({
  type : SET_MAP_ZOOM, zoom
});

// Updates the modular search queries for Admin Notifications
export const updateNotificationsSearchQueries = (searchParams: NotificationsFilters) => ({
  type : UPDATE_NOTIFICATIONS_SEARCH_QUERIES, searchParams
});

// Resets the Peg Labels filters state
export const resetNotificationsSearchQueries = () => ({
  type : RESET_NOTIFICATIONS_SEARCH_QUERIES
});

/**
 * @getNotification
 *
 * fetches notification for this account to be displayed in the Devices
 * section
 */
export const getAlertsNotifications = ({
  pageId = 1,
  pageSize = 20,
  callback: callbackFn,
  includeSubGroups = false,
  filter = {},
  sort,
  accountId
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_NOTIFICATION_REQUEST});
    const {
      notifications: {
        search
      }
    } = getState();
    const data: LoadAlertsNotificationParams = {
      pageId,
      pageSize,
      accountId,
      includeSubGroups
    };
    if (sort) {
      data.sortOn = sort;
    }
    if (filter) {
      data.filterOn = filter;
    }
    data.filterOn = {
      ...data.filterOn,
      ...createModularSearchFilters(search)
    };
    try {
      const {items, totalCount} = await loadAlertNotification(data);
      dispatch({type: GET_NOTIFICATION_RESPONSE});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      callbackFn({error: err, items: [], totalCount: 0});
      dispatch({type: GET_NOTIFICATION_ERROR, error: err});
      return error(err);
    }
  };
};

/**
 * @viewAlertsNotification
 *
 * Invoked when the View deviceFile editor is to be displayed or removed.
 */
export const viewAlertsNotification = (notification: AlertNotifications, show = true) => ({
  type : VIEW_NOTIFICATION, notification, show
});

/**
 * Invoked whenever user click on View Sub Accounts toggle button
 */
export const setNotificationsViewSubGroups = (viewSubGroups: boolean = false) => ({
  type : SET_VIEW_SUB_GROUPS, viewSubGroups
});

/**
 * Invoked whenever user change the rows per page dropdown
 */
export const setNotificationsGridPageSize = (pageSize: number) => ({
  type : SET_GRID_PAGE_SIZE, pageSize
});

/* Reducers */
export interface NotificationsAction {
  type: string;
  result?: any;
  error?: ServiceError;
  show?: boolean;
};

export interface NotificationAction extends NotificationsAction {
  notification?: AlertNotification;
  searchParams?: NotificationsFilters;
};

export interface NotificationActionState extends AsyncState {
  selected: AlertNotification | AlertNotification[] | null;
  result: AlertNotification | AlertNotification[] | null;
  isEditing: boolean;
  record?: AlertNotification | AlertNotification[] | null;
};

export interface NotificationState extends PageState {
  notification: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    includeSubGroups: boolean,
    pageSize: number
  },
  view: NotificationActionState,
  map: {
    initialized: boolean;
    basemap: BasemapTypes;
    extent: null;
    center: null;
    zoom: number;
  },
  search: NotificationsFilters
};

// defines the initial state for the reducer ...
export const initialState: NotificationState = {
  isInitialized : false,
  notification  : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    includeSubGroups    : false,
    pageSize            : gridDefaultPageSize
  },
  view : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  map : {
    initialized : false,
    basemap     : BasemapTypes.grayVector,
    extent      : null,
    center      : null,
    zoom        : 6
  },
  search : {
    severity               : null,
    alertTypeName          : null,
    esn                    : null,
    alertConfigurationName : null,
    createdOn              : null,
    assetName              : null
  }
};

// defines the preference key for the alert notification page ...
export const PREFERENCE = 'alerts-notifications';

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_NOTIFICATION] : draft => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
  },
  [SET_GRID_COLUMNS] : ({grid, notification}: NotificationState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : notification.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: NotificationState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [GET_NOTIFICATION_REQUEST] : ({notification}: NotificationState) => {
    notification.loading = true;
  },
  [GET_NOTIFICATION_RESPONSE] : ({notification}: NotificationState) => {
    notification.loading = false;
  },
  [GET_NOTIFICATION_ERROR] : ({notification}: NotificationState, {error}: NotificationAction) => {
    notification.error = error;
    notification.loading = false;
  },
  [VIEW_NOTIFICATION] : ({view}: NotificationState, {notification, show}: NotificationAction) => {
    view.selected  = show ? notification : null;
    view.isEditing = show;
    view.result    = null;
  },
  [VIEW_NOTIFICATION_REQUEST] : ({view}: NotificationState) => {
    view.loading = true;
    view.error   = view.result = null;
  },
  [VIEW_NOTIFICATION_RESPONSE] : ({view}: NotificationState, {result}: NotificationAction) => {
    view.loading   = false;
    view.result    = result;
  },
  [VIEW_NOTIFICATION_ERROR] : ({view}: NotificationState, {error}: NotificationAction) => {
    view.loading   = false;
    view.error     = error;
  },
  [SET_MAP_INITIALIZED] : (draft: NotificationState) => {
    draft.map.initialized = true;
  },
  [SET_MAP_BASEMAP] : (draft: NotificationState, {basemap}) => {
    draft.map.basemap = basemap;
  },
  [SET_MAP_INITIALIZED] : (draft: NotificationState) => {
    draft.map.initialized = true;
  },
  [SET_MAP_EXTENT] : (draft: NotificationState, {extent}) => {
    draft.map.extent = extent;
  },
  [SET_MAP_ZOOM] : (draft: NotificationState, {zoom}) => {
    draft.map.zoom = zoom;
  },
  [UPDATE_NOTIFICATIONS_SEARCH_QUERIES] : ({search}: NotificationState, {searchParams}) => {
    search.severity               = searchParams.severity || null;
    search.alertTypeName          = searchParams.alertTypeName || null;
    search.esn                    = searchParams.esn || null;
    search.alertConfigurationName = searchParams.alertConfigurationName || null;
    search.createdOn              = searchParams.createdOn || null;
    search.assetName              = searchParams.assetName || null;
  },
  [RESET_NOTIFICATIONS_SEARCH_QUERIES] : ({search}: NotificationState) => {
    search.severity               = null;
    search.alertTypeName          = null;
    search.esn                    = null;
    search.alertConfigurationName = null;
    search.createdOn              = null;
    search.assetName              = null;
  },
  [SET_VIEW_SUB_GROUPS] : ({grid}: NotificationState, {viewSubGroups}) => {
    grid.includeSubGroups = viewSubGroups;
  },
  [SET_GRID_PAGE_SIZE] : ({grid}: NotificationState, {pageSize}) => {
    grid.pageSize = pageSize;
  }
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: NotificationState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
